import { TableCell, TableRow, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import Select, { OptionTypeBase, ValueType } from "react-select";
import Icons8CurlyBrackets from "../../components/icons/svg/windows-10/Icons8CurlyBrackets";
import Icons8Rename from "../../components/icons/svg/windows-10/Icons8Rename";
import { AscendingSortingIcon, DescendingSortingIcon, SortingIcon } from "../../components/icons/themes/materialsharp";
import { AddIcon, DeleteIcon, EditIcon, TrashIcon } from "../../components/icons/themes/windows10";
import { DestinationKey } from "../../destinations/types";
import { makeRequest } from "../../util";
import {
    TransformDefinition,
    TransformDefinitionSet,
    TransformedEvent,
    UntransformedEvent,
} from "../transformationDefinition";
import {
    transformationOperationIcons,
    transformationOperations,
    transformationOperationText,
    TransformationOperationType,
} from "../types";
import { BeforeAndAfterComparison } from "./BeforeAndAfterEventComparison";
import { TransformationDeleteModal } from "./TransformationDeleteModal";

const useStyles = makeStyles((theme) => ({
    destinationIcon: {
        height: 30,
        width: 30,
        verticalAlign: "middle",
        textAlign: "center",
    },
    transformationListItem: {
        "& code": {
            transition: ".5s all",
            maxWidth: 180,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            verticalAlign: "middle",
            display: "inline-block",
        },
    },
    transformationListItemClosed: {
        "&:hover": {
            backgroundColor: "#EEE",
            "& code": {
                backgroundColor: "#DDD",
            },
        },
    },
    transformationListItemIconWrapper: {
        width: 40,
        height: 40,
        backgroundColor: "#F4F5FB",
        color: "#3F51B5",
        borderRadius: "50%",
        marginRight: theme.spacing(2),
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    transformationListItemIcon: {
        fontSize: 28,
        verticalAlign: "middle",
        fill: "#3F51B5",
    },
    transformationListItemSummary: {
        marginLeft: theme.spacing(1),
    },
    transformationListItemDescription: {
        color: "#666",
        fontSize: 10,
        fontWeight: "lighter",
    },
    addNewTransformationButton: {
        cursor: "pointer",
        borderRadius: theme.spacing(2),
        border: "none",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "white",
        transition: "all .5s",
        "&:hover": {
            backgroundColor: "#F4F5FB",
        },
    },
    innerTableOfTransforms: {
        display: "flex",
    },
    transformationIntention: {
        backgroundColor: "#DBF1D7",
        borderRadius: theme.spacing(.5),
        color: "#419233",
        padding: theme.spacing(.75),
        "& svg": {
            verticalAlign: "sub",
            fontSize: 18,
            fill: "#419233",
        },
    },
}));

export type TransformationPageFilter = {
    type: "destinationName" | "eventDefId";
    value: string;
};

export type TransformationPageSortColumns = "destinationName" | "createdAt" | "eventDefName" | "intention";
export type TransformationPageSort = {
    column: TransformationPageSortColumns;
    direction: "asc" | "desc";
};

export const SortableTableColumn: React.FC<{
    column: TransformationPageSortColumns;
    handleClick: (column: TransformationPageSortColumns) => void;
    currentSort: TransformationPageSort;
    text: string;
}> = ({ handleClick, currentSort, column, text }) => {
    const internalHandleClick = () => {
        handleClick(column);
    };
    const classes = useStyles();

    const Icon = currentSort.column === column ? (currentSort.direction === "asc" ? AscendingSortingIcon : DescendingSortingIcon) : SortingIcon;
    const iconRendered = <Icon
        className={clsx("ml-2 fill-gray-600 align-middle", { "text-gray-700": currentSort.column === column })}/>;

    return (
        <TableCell className="cursor-pointer" onClick={internalHandleClick}>
            {text} {iconRendered}
        </TableCell>
    );
};


const TableRowForTransformation: React.FC<{
    reloadAllTransforms: () => void;
    transformation: TransformDefinition;
    transformationSet: TransformDefinitionSet;
}> = ({ reloadAllTransforms, transformation, transformationSet }) => {
    const classes = useStyles();
    const alert = useAlert();
    const TransformationIcon = transformation.operationIcon();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const confirmDeleteTransform = () => {
        setIsSaving(true);
        makeRequest<any>("delete_transformation", {
            id: transformation.ID,
            transformationSetID: transformationSet.id,
        })
            .then((response) => {
                setIsEditing(false);
                setIsSaving(false);
                reloadAllTransforms();
                setDeleteModalOpen(false);
            })
            .catch((error: string) => {
                setDeleteModalOpen(false);
                alert.error("Something went wrong!");
                console.log(error);
                Sentry.captureException(error);
                setIsSaving(false);
            });
    };

    const saveTransform: saveTransformFn = ({
        targetValue,
        operation,
        operandValue,
    }) => {
        setIsSaving(true);
        makeRequest<any>("save_transformation", {
            id: transformation.ID,
            transformationSetID: transformationSet.id,
            targetValue,
            operation,
            operandValue,
        })
            .then((response) => {
                console.log(response);
                setIsEditing(false);
                setIsSaving(false);
                reloadAllTransforms();
            })
            .catch((error: string) => {
                alert.error("Something went wrong!");
                console.log(error);
                Sentry.captureException(error);
                setIsEditing(false);
                setIsSaving(false);
            });
    };

    const deleteModal = <TransformationDeleteModal header="Are you sure you want to delete this transformation?"
                                                   isOpen={deleteModalOpen}
                                                   onConfirm={confirmDeleteTransform}
                                                   onClose={() => setDeleteModalOpen(false)}/>;

    if (isEditing) {
        return (
            <>
                {deleteModal}
                <EditTransform
                    transformationSet={transformationSet}
                    editingID={transformation.ID}
                    initialOperandValue={transformation.operation.operandValue}
                    initialTargetValue={transformation.operation.targetValue}
                    initialOperationValue={transformation.operation.operationType}
                    cancel={() => setIsEditing(false)}
                    isSaving={isSaving}
                    save={saveTransform}
                    onDelete={() => setDeleteModalOpen(true)}
                />
            </>
        );
    } else {
        return (
            <div
                onClick={() => setIsEditing(true)}
                className={clsx(
                    "group flex justify-between items-center",
                    "w-full py-4 px-6 flex items-center cursor-pointer transition rounded",
                    classes.transformationListItem,
                    classes.transformationListItemClosed,
                )}>
                {deleteModal}
                <div className="flex items-center">
                    <div className={classes.transformationListItemIconWrapper}>
                        <TransformationIcon className={classes.transformationListItemIcon}/>
                    </div>
                    <div>
                        {transformation.operationShortDisplay()}
                        <span className={classes.transformationListItemSummary}>
                            {transformation.exampleResultDisplay(transformationSet)}
                        </span>
                    </div>
                </div>
                <span
                    className="text-white group-hover:text-gray-600 transition-all duration-500 justify-center items-center">
                <EditIcon/>
            </span>
            </div>
        );
    }
};

type OperationOptionType = {
    value: TransformationOperationType;
    label: string;
    icon: React.ForwardRefExoticComponent<any>;
};

const operationOptions: OperationOptionType[] = _.map(transformationOperations, (op) => ({
    value: op,
    label: transformationOperationText[op],
    icon: transformationOperationIcons[op],
}));

const castPropertyValueOptions: OptionTypeBase[] = [
    {
        value: "number",
        label: "Number",
    },
    {
        value: "boolean",
        label: "True/False",
    },
    {
        value: "string",
        label: "Text",
    },
];

const EditTransform: React.FC<{
    transformationSet: TransformDefinitionSet;
    cancel: () => void;
    save: saveTransformFn;
    isSaving: boolean;
    onDelete?: () => void;
    initialOperationValue?: TransformationOperationType | null;
    initialTargetValue?: string | null;
    initialOperandValue?: string | boolean | number | null;
    editingID?: string | null;
}> = ({
    transformationSet,
    cancel,
    save,
    isSaving,
    onDelete,
    initialOperandValue,
    initialOperationValue,
    initialTargetValue,
    editingID,
}) => {
    const [operationValue, setOperationValue] = useState<ValueType<OperationOptionType> | null>(null);
    const [targetValue, setTargetValue] = useState("");
    const [operandValue, setOperandValue] = useState<string>("");
    const [operandValueForCasting, setOperandValueForCasting] = useState<ValueType<OptionTypeBase>>(null);

    useEffect(() => {
        // If initialized with values, set up the state correctly.
        if (initialOperationValue) {
            setOperationValue(_.find(operationOptions, (opt) => opt.value === initialOperationValue) || null);
        }
        if (initialTargetValue) {
            setTargetValue(initialTargetValue);
        }
        if (initialOperandValue) {
            setOperandValue(initialOperandValue as string);
        }
        if (initialOperandValue && initialOperationValue === "castPropertyValue") {
            setOperandValueForCasting(_.find(castPropertyValueOptions, (opt) => opt.value === initialOperandValue) || null);
        }
    }, []);

    const showTargetValue = operationValue && ([
            "replacePropertyKey",
            "castPropertyValue",
            "uppercasePropertyValue",
            "lowercasePropertyValue",
            "deleteProperty",
            // @ts-ignore
        ].indexOf(operationValue.value) !== -1 ||
        // @ts-ignore
        (operationValue.value === "replaceName" && !transformationSet.eventDefId));

    const showOperandValue = operationValue && [
        "replaceName",
        "replacePropertyKey",
        // @ts-ignore
    ].indexOf(operationValue.value) !== -1;

    // @ts-ignore
    const showCastSelect = operationValue && operationValue.value === "castPropertyValue";

    const getOperandValueToSave = () => {
        // @ts-ignore
        if (operationValue.value === "castPropertyValue") {
            if (operandValueForCasting) {
                // @ts-ignore
                return operandValueForCasting.value;
            } else {
                return null;
            }
        }
        return operandValue || null;
    };

    const saveHandler = () => {
        if (!operationValue) {
            return false;
        }

        save({
            operandValue: getOperandValueToSave(),
            // @ts-ignore
            operation: operationValue.value,
            targetValue: targetValue || null,
        });
        setOperationValue(null);
        setTargetValue("");
        setOperandValue("");
        setOperandValueForCasting(null);
    };

    const allowedOperationOptions = transformationSet.eventDefId ? operationOptions : _.filter(operationOptions, (opt) => opt.value !== "replaceName");

    return (
        <div className={clsx("rounded-lg border pt-2 pb-4 px-6 my-2", { "opacity-50": isSaving })}>
            <div className="text-md text-gray-800 font-bold mb-4 flex justify-between items-center">
                {editingID && <span>Edit transformation</span>}
                {!editingID && <span>Add new transformation</span>}
                <Tooltip title="Cancel">
                    <span onClick={cancel}
                          className="cursor-pointer w-8 h-8 flex justify-center items-center text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full">
                        <DeleteIcon/>
                    </span>
                </Tooltip>
            </div>
            <Select options={allowedOperationOptions} value={operationValue} onChange={setOperationValue}/>


            {showTargetValue &&
            <input
                /* @ts-ignore */
              placeholder={operationValue && operationValue.value === "replaceName" ? "Original Event Name..." : "Original Property..."}
              className="font-mono py-2 px-2 w-full mt-2 border rounded tracking-tight"
              value={targetValue} onChange={(e) => setTargetValue(e.target.value)}/>}

            {showOperandValue &&
            <input
              className="py-2 px-2 w-full mt-2 font-mono border rounded tracking-tight"
              placeholder="Rename to..."
              value={operandValue}
              onChange={(e) => setOperandValue(e.target.value)}
            />
            }

            {/* @ts-ignore */}
            {showCastSelect && <Select className="mt-2" placeholder="Change to..." value={operandValueForCasting}
                                       options={castPropertyValueOptions} onChange={setOperandValueForCasting}/>}

            <div className="flex justify-between items-center mt-2">
                {editingID && <>
                  <button className="w-1/3 rounded btn py-2 bg-red-200 text-red-900 hover:bg-red-300 text-center"
                          onClick={onDelete}>
                    <TrashIcon style={{ position: "relative", top: -1 }} className="mr-1"/>
                    Delete
                  </button>
                  <div className="w-4 flex-shrink-0 flex-grow-0"/>
                </>}
                <button
                    className="rounded w-full btn py-2 text-center bg-purple-200 text-purple-900 hover:bg-purple-300 "
                    onClick={saveHandler}>Save
                </button>
            </div>
        </div>
    );
};

type saveTransformFn = (
    {
        targetValue,
        operation,
        operandValue,
    }: {
        targetValue: string | null;
        operation: string | null;
        operandValue: string | null;
    }) => void;

const InnerTableOfTransformations: React.FC<{
    transformationSet: TransformDefinitionSet;
    availableDestinations: Record<DestinationKey, string>;
    input: UntransformedEvent;
    output: TransformedEvent;
    isOpen: boolean;
    reloadAllTransforms: () => void;
}> = ({ reloadAllTransforms, availableDestinations, input, output, isOpen = false, transformationSet }) => {
    const classes = useStyles();
    const alert = useAlert();
    const [addingNew, setAddingNew] = useState(transformationSet.transformations.length === 0);
    const [isSaving, setIsSaving] = useState(false);

    const saveNewTransform: saveTransformFn = ({
        targetValue,
        operation,
        operandValue,
    }) => {
        setIsSaving(true);
        makeRequest<any>("save_transformation", {
            targetValue: targetValue,
            operation: operation,
            operandValue: operandValue,
            transformationSetID: transformationSet.id,
        })
            .then((response) => {
                setIsSaving(false);
                setAddingNew(false);
                reloadAllTransforms();
            })
            .catch((error: string) => {
                alert.error("Something went wrong!");
                console.error(error);
                Sentry.captureException(error);
                setIsSaving(false);
            });
    };

    return (
        <TableRow>
            {isOpen && (
                <TableCell colSpan={9999}>
                    <div className={classes.innerTableOfTransforms}>
                        <div className="w-1/3" style={{ verticalAlign: "top" }}>
                            <div className="flex flex-col">
                                {transformationSet.orderedTransformations().map((transformation, index) => {
                                    return (
                                        <TableRowForTransformation
                                            reloadAllTransforms={reloadAllTransforms}
                                            key={index}
                                            transformation={transformation}
                                            transformationSet={transformationSet}/>
                                    );
                                })}
                                {transformationSet.transformations.length === 0 && !addingNew && (
                                    <div className="text-gray-500 text-center font-bold mb-4">No transformations</div>
                                )}
                                {
                                    addingNew ? (
                                        <EditTransform transformationSet={transformationSet}
                                                       isSaving={isSaving}
                                                       cancel={() => setAddingNew(false)}
                                                       save={saveNewTransform}/>
                                    ) : (
                                        <div key={-1} onClick={() => setAddingNew(true)}
                                             className={clsx(
                                                 "cursor-pointer rounded border-0 w-full items-center justify-center",
                                                 "bg-white transition-all duration-500 hover:bg-purple-50",
                                                 classes.transformationListItem,
                                                 "w-full py-4 px-6 flex items-center cursor-pointer transition",
                                                 "rounded")}>
                                            <div className={classes.transformationListItemIconWrapper}>
                                                <AddIcon className={classes.transformationListItemIcon}/>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                        <div className="w-2/3">
                            <BeforeAndAfterComparison before={input} after={output}/>
                        </div>
                    </div>
                </TableCell>
            )}
        </TableRow>
    );
};

const TransformationSetDescription: React.FC<{ transformationSet: TransformDefinitionSet }> = ({ transformationSet }) => {
    if (transformationSet.description) {
        return <span>transformationSet.description</span>;
    } else {
        const {propertyTransformations, eventTransformations} = transformationSet.operationSummary();
        return (
        <div className="text-xs text-gray-700">
            {eventTransformations.count ? (
                <div className="flex items-center mb-1">
                    <Icons8Rename className="mr-1 text-gray-500"/>
                    Rename to <code className="mx-1 text-xs bg-gray-400">{eventTransformations.sample.operation.operandValue}</code>
                </div>
            ) : null}
            {propertyTransformations.count ? (
                <div className="flex items-center mb-1">
                    <Icons8CurlyBrackets className="mr-1 text-gray-500"/>
                    {propertyTransformations.count === 1 ? (
                        <>
                            {propertyTransformations.sample.operation.shortName()}
                            <code className="mx-1 text-xs bg-gray-400">{propertyTransformations.sample.operation.targetValue}</code>
                            {propertyTransformations.sample.operation.operandValue !== null && (
                                <>to <code className="mx-1 text-xs bg-gray-400">{propertyTransformations.sample.operation.operandValue}</code></>
                            )}
                        </>
                    ) : (
                        <>
                            {propertyTransformations.count} property transformations
                        </>
                    )}
                </div>
            ) : null}
        </div>
        );
    }
};

export const TableRowForTransformationSet: React.FC<{
    transformationSet: TransformDefinitionSet;
    reloadAllTransforms: () => void;
    availableDestinations: Record<DestinationKey, string>;
    isOpen: boolean;
    onOpen: (id: string) => void;
}> = ({ reloadAllTransforms, transformationSet, isOpen, onOpen, availableDestinations }) => {
    const classes = useStyles();
    const alert = useAlert();
    const [sampleEvent, setSampleEvent] = useState(transformationSet.getSampleInputEvent());
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const Icon = transformationSet.icon();
    const icon = <Icon className="align-middle mr-2 fill-gray-600"/>;
    const DestIcon = transformationSet.destinationIcon();
    const SourceIcon = transformationSet.eventSourceIcon();
    const [isSaving, setIsSaving] = useState(false);
    const [output, setOutput] = useState(transformationSet.perform(sampleEvent.eventName, sampleEvent.eventProperties));

    const handleOnOpen = () => {
        if (isOpen) {
            onOpen("");
        } else {
            onOpen(transformationSet.id);
        }
    };

    const confirmDeleteTransform = () => {
        setIsSaving(true);
        setDeleteModalOpen(false);
        makeRequest<any>("delete_transformation_set", {
            id: transformationSet.id,
        })
            .then((response) => {
                setIsSaving(false);
                reloadAllTransforms();
            })
            .catch((error: string) => {
                alert.error("Something went wrong!");
                console.log(error);
                Sentry.captureException(error);
                setIsSaving(false);
            });
    };

    const startDelete = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setDeleteModalOpen(true);
        event.stopPropagation();
    };

    useEffect(() => setSampleEvent(transformationSet.getSampleInputEvent()), [transformationSet]);
    useEffect(() => setOutput(transformationSet.perform(sampleEvent.eventName, sampleEvent.eventProperties)), [transformationSet, sampleEvent]);

    return (
        <>
            <TransformationDeleteModal header="Are you sure you want to delete this transformation set?"
                                       body="This action is irreversable."
                                       isOpen={deleteModalOpen}
                                       onConfirm={confirmDeleteTransform}
                                       onClose={() => setDeleteModalOpen(false)}/>
            <TableRow className={clsx("cursor-pointer hover:bg-gray-100", { "border-b-0 bg-gray-100": isOpen })}
                      onClick={handleOnOpen}>
                <TableCell><span
                    className={classes.transformationIntention}>{icon}{transformationSet.intentionDisplay()}</span></TableCell>
                <TableCell><TransformationSetDescription transformationSet={transformationSet}/></TableCell>
                <TableCell>{
                    SourceIcon ? <Tooltip title={transformationSet.eventSourceDisplay()}>
                    <SourceIcon className="fill-gray-600 align-middle text-xl mr-1"/></Tooltip>
                    : null
                } {transformationSet.eventDefNameDisplay()}</TableCell>
                <TableCell>{DestIcon ? <Tooltip title={transformationSet.destinationDisplay()}><DestIcon
                    className={classes.destinationIcon}/></Tooltip> : transformationSet.destinationDisplay()}</TableCell>
                <TableCell>{new Date((transformationSet.createdAt as string)).toLocaleDateString()}</TableCell>
                <TableCell className="flex justify-center">
                    <div onClick={startDelete}
                         className="w-12 h-12 p-2 rounded-full hover:bg-red-100 flex items-center justify-center hover:text-red-800 text-red-600 text-2xl">
                        <TrashIcon/>
                    </div>
                </TableCell>
            </TableRow>
            <InnerTableOfTransformations
                reloadAllTransforms={reloadAllTransforms}
                isOpen={isOpen}
                availableDestinations={availableDestinations}
                input={sampleEvent}
                output={output}
                transformationSet={transformationSet}
            />
        </>
    );
};